import React, { useState, useEffect } from 'react';
import { sharedStyles, getStatusColor, formatDate } from '../styles/sharedStyles';
import { useApi } from '../hooks/useApi';
import { useUser } from '../hooks/useUser';
import { isMachineSchedulingUnavailable, machineSchedulingUnavailableNote } from '../utils/machineScheduling';

const API_BASE_URL = process.env.API_BASE_URL;

const Machines = () => {
  const api = useApi();
  const { user } = useUser();
  const canManage = user?.role === 'admin';
  const hideMachineRateColumns = user?.role === 'foreman';
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingMachine, setEditingMachine] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('Active');
  const [schedulingClearLoading, setSchedulingClearLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    model: '',
    serialNumber: '',
    manufacturer: '',
    purchaseDate: '',
    status: 'Active',
    lastMaintenanceDate: '',
    nextMaintenanceDate: '',
    location: '',
    notes: '',
    dayRate: '',
    hourlyRate: '',
    loadRate: ''
  });

  // Fetch machines with pagination and filters
  const fetchMachines = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        page: currentPage,
        limit: 10,
        search: searchTerm,
        ...(selectedStatus && { status: selectedStatus })
      });

      const response = await api({
        method: 'get',
        url: `/machines?${params}`
      });
      
      if (response.data.success) {
        setMachines(response.data.data.machines);
        setTotalPages(response.data.data.pagination.totalPages);
      }
    } catch (err) {
      console.error('Error fetching machines:', err);
      setError(err.response?.data?.message || 'Failed to fetch machines');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMachines();
  }, [currentPage, searchTerm, selectedStatus]);

  // Add effect to handle body scroll when modal is open
  useEffect(() => {
    if (showModal || showEditModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showModal, showEditModal]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      
      const response = await api({
        method: 'post',
        url: `/machines`,
        data: formData
      });
      
      if (response.data.success) {
        await fetchMachines();
        handleCloseModal();
      }
    } catch (err) {
      console.error('Error creating machine:', err);
      setError(err.response?.data?.message || 'Failed to create machine');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (machine) => {
    setEditingMachine(machine);
    setFormData({
      name: machine.name || '',
      model: machine.model || '',
      serialNumber: machine.serialNumber || '',
      manufacturer: machine.manufacturer || '',
      purchaseDate: machine.purchaseDate ? machine.purchaseDate.split('T')[0] : '',
      status: machine.status || '',
      lastMaintenanceDate: machine.lastMaintenanceDate ? machine.lastMaintenanceDate.split('T')[0] : '',
      nextMaintenanceDate: machine.nextMaintenanceDate ? machine.nextMaintenanceDate.split('T')[0] : '',
      location: machine.location || '',
      notes: machine.notes || '',
      dayRate: machine.dayRate || '',
      hourlyRate: machine.hourlyRate || '',
      loadRate: machine.loadRate || ''
    });
    setShowEditModal(true);
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      
      const response = await api({
        method: 'put',
        url: `/machines/${editingMachine.id}`,
        data: formData
      });
      
      if (response.data.success) {
        await fetchMachines();
        handleCloseEditModal();
      }
    } catch (err) {
      console.error('Error updating machine:', err);
      setError(err.response?.data?.message || 'Failed to update machine');
    } finally {
      setLoading(false);
    }
  };

  const handleClearSchedulingBlock = async () => {
    if (!editingMachine?.id || !canManage) return;
    try {
      setSchedulingClearLoading(true);
      setError(null);
      const response = await api({
        method: 'put',
        url: `/machines/${editingMachine.id}/scheduling-block`,
        data: { schedulingBlocked: false }
      });
      if (response.data.success && response.data.data) {
        setEditingMachine((prev) => (prev ? { ...prev, ...response.data.data } : prev));
        await fetchMachines();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Could not clear scheduling block');
    } finally {
      setSchedulingClearLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this machine?')) {
      try {
        setLoading(true);
        const response = await api({
          method: 'delete',
          url: `/machines/${id}`
        });
        
        if (response.data.success) {
          fetchMachines();
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to delete machine');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({
      name: '',
      model: '',
      serialNumber: '',
      manufacturer: '',
      purchaseDate: '',
      status: 'Active',
      lastMaintenanceDate: '',
      nextMaintenanceDate: '',
      location: '',
      notes: '',
      dayRate: '',
      hourlyRate: '',
      loadRate: ''
    });
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingMachine(null);
    setSchedulingClearLoading(false);
    setFormData({
      name: '',
      model: '',
      serialNumber: '',
      manufacturer: '',
      purchaseDate: '',
      status: 'Active',
      lastMaintenanceDate: '',
      nextMaintenanceDate: '',
      location: '',
      notes: '',
      dayRate: '',
      hourlyRate: '',
      loadRate: ''
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  return (
    <div style={sharedStyles.pageContainer} className="container-fluid px-2 px-md-4 py-4">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-3">
        <h2 style={sharedStyles.pageTitle}>Machine Management</h2>
        {canManage && (
          <button 
            className="btn"
            style={sharedStyles.button.primary}
            onClick={() => setShowModal(true)}
          >
            <i className="bi bi-plus-circle me-2"></i>
            Add Machine
          </button>
        )}
      </div>

      {/* Search and Filter Section */}
      <div className="card mb-4" style={sharedStyles.card}>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-6">
              <input
                type="text"
                className="form-control"
                placeholder="Search by name, model, or serial number"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <select
                className="form-select"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                <option value="">All Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="Maintenance">Maintenance</option>
                <option value="Repair">Repair</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center my-4">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      {/* Machines Table */}
      <div className="card" style={sharedStyles.card}>
        <div className="card-body">
          <div className="table-responsive">
            <table className="table align-middle mb-0" style={sharedStyles.table}>
              <thead>
                <tr>
                  <th>
                    Name <i className="bi bi-sort-alpha-down" title="Sorted alphabetically A-Z"></i>
                  </th>
                  <th>Model</th>
                  <th>Serial Number</th>
                  <th>Status</th>
                  <th>Scheduling</th>
                  <th>Location</th>
                  <th>Last Maintenance</th>
                  <th>Next Maintenance</th>
                  {!hideMachineRateColumns && (
                    <>
                      <th>Day Rate</th>
                      <th>Rate per Load</th>
                      <th>Hourly Rate</th>
                    </>
                  )}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {/* Machines are displayed in alphabetical order (A-Z) */}
                {machines
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map(machine => {
                    const statusStyle = getStatusColor(machine.status);
                    return (
                      <tr key={machine.id}>
                        <td>{machine.name}</td>
                        <td>{machine.model}</td>
                        <td>{machine.serialNumber || '-'}</td>
                        <td>
                          <span 
                            style={{
                              ...sharedStyles.statusBadge,
                              backgroundColor: statusStyle.bg,
                              color: statusStyle.text
                            }}
                          >
                            {machine.status}
                          </span>
                        </td>
                        <td>
                          {isMachineSchedulingUnavailable(machine) ? (
                            <span
                              className="badge text-bg-warning text-dark"
                              title={machineSchedulingUnavailableNote(machine) || 'Unavailable for scheduling'}
                            >
                              Unavailable
                            </span>
                          ) : (
                            <span className="text-muted small">—</span>
                          )}
                        </td>
                        <td>{machine.location || '-'}</td>
                        <td>{machine.lastMaintenanceDate ? formatDate(machine.lastMaintenanceDate) : '-'}</td>
                        <td>{machine.nextMaintenanceDate ? formatDate(machine.nextMaintenanceDate) : '-'}</td>
                        {!hideMachineRateColumns && (
                          <>
                            <td>{machine.dayRate ? formatCurrency(machine.dayRate) : '-'}</td>
                            <td>{machine.loadRate ? formatCurrency(machine.loadRate) : '-'}</td>
                            <td>{machine.hourlyRate ? formatCurrency(machine.hourlyRate) : '-'}</td>
                          </>
                        )}
                        <td>
                          {canManage ? (
                            <div className="btn-group">
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => handleEdit(machine)}
                                title="Edit Machine"
                              >
                                <i className="bi bi-pencil"></i>
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDelete(machine.id)}
                                title="Delete Machine"
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </div>
                          ) : (
                            <span className="text-muted small">View only</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <nav className="mt-4" style={sharedStyles.pagination} aria-label="Machine table pagination">
            <ul className="pagination justify-content-center mb-0 flex-wrap">
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button 
                  className="page-link" 
                  onClick={() => setCurrentPage(prev => prev - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
              </li>
              {[...Array(totalPages)].map((_, index) => (
                <li key={index + 1} className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}>
                  <button 
                    className="page-link"
                    onClick={() => setCurrentPage(index + 1)}
                  >
                    {index + 1}
                  </button>
                </li>
              ))}
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button 
                  className="page-link"
                  onClick={() => setCurrentPage(prev => prev + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      {/* Add Machine Modal */}
      {showModal && (
        <>
          <div 
            className="modal-backdrop fade show" 
            style={sharedStyles.modal.overlay}
            onClick={handleCloseModal}
          ></div>
          <div 
            className="modal fade show" 
            style={{ 
              display: 'block',
              zIndex: 1500
            }} 
            tabIndex="-1"
          >
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content" style={sharedStyles.modal.content}>
                <div className="modal-header border-0 pb-0">
                  <h5 style={sharedStyles.cardTitle}>Add New Machine</h5>
                  <button 
                    type="button" 
                    className="btn-close"
                    onClick={handleCloseModal}
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body">
                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Name</label>
                        <input
                          type="text"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Model</label>
                        <input
                          type="text"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="model"
                          value={formData.model}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Serial Number</label>
                        <input
                          type="text"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="serialNumber"
                          value={formData.serialNumber}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Manufacturer</label>
                        <input
                          type="text"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="manufacturer"
                          value={formData.manufacturer}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Purchase Date</label>
                        <input
                          type="date"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="purchaseDate"
                          value={formData.purchaseDate}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Status</label>
                        <select
                          className="form-select"
                          style={sharedStyles.formSelect}
                          name="status"
                          value={formData.status}
                          onChange={handleInputChange}
                        >
                          <option value="Active">Active</option>
                          <option value="Inactive">Inactive</option>
                          <option value="Maintenance">Maintenance</option>
                          <option value="Repair">Repair</option>
                        </select>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Last Maintenance Date</label>
                        <input
                          type="date"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="lastMaintenanceDate"
                          value={formData.lastMaintenanceDate}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Next Maintenance Date</label>
                        <input
                          type="date"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="nextMaintenanceDate"
                          value={formData.nextMaintenanceDate}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Location</label>
                        <input
                          type="text"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="location"
                          value={formData.location}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Day Rate</label>
                        <input
                          type="number"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="dayRate"
                          value={formData.dayRate}
                          onChange={handleInputChange}
                          min="0"
                          step="0.01"
                          placeholder="Enter day rate"
                        />
                      </div>
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Hourly Rate</label>
                        <input
                          type="number"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="hourlyRate"
                          value={formData.hourlyRate}
                          onChange={handleInputChange}
                          min="0"
                          step="0.01"
                          placeholder="Enter hourly rate"
                        />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Rate per Load</label>
                        <input
                          type="number"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="loadRate"
                          value={formData.loadRate}
                          onChange={handleInputChange}
                          min="0"
                          step="0.01"
                          placeholder="Enter rate per load"
                        />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-12 mb-3">
                        <label style={sharedStyles.formLabel}>Notes</label>
                        <textarea
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="notes"
                          value={formData.notes}
                          onChange={handleInputChange}
                          rows="3"
                        />
                      </div>
                    </div>

                    <div className="text-end mt-4">
                      <button 
                        type="button" 
                        className="btn me-2"
                        style={sharedStyles.button.secondary}
                        onClick={handleCloseModal}
                        disabled={loading}
                      >
                        Cancel
                      </button>
                      <button 
                        type="submit" 
                        className="btn"
                        style={sharedStyles.button.primary}
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Adding...
                          </>
                        ) : (
                          'Add Machine'
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Edit Machine Modal */}
      {showEditModal && (
        <>
          <div 
            className="modal-backdrop fade show" 
            style={sharedStyles.modal.overlay}
            onClick={handleCloseEditModal}
          ></div>
          <div 
            className="modal fade show" 
            style={{ 
              display: 'block',
              zIndex: 1500
            }} 
            tabIndex="-1"
          >
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content" style={sharedStyles.modal.content}>
                <div className="modal-header border-0 pb-0">
                  <h5 style={sharedStyles.cardTitle}>Edit Machine</h5>
                  <button 
                    type="button" 
                    className="btn-close"
                    onClick={handleCloseEditModal}
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body">
                  {editingMachine && isMachineSchedulingUnavailable(editingMachine) && (
                    <div className="alert alert-warning d-flex flex-column flex-md-row justify-content-between align-items-start gap-2 mb-3">
                      <div>
                        <strong className="d-block">Unavailable for scheduling</strong>
                        <span className="small d-block">
                          {machineSchedulingUnavailableNote(editingMachine) ||
                            editingMachine.schedulingBlockReason ||
                            'This machine cannot be booked on new schedule entries.'}
                        </span>
                        <p className="small text-muted mb-0 mt-2">
                          When it is repaired and ready for work, mark it available below.
                        </p>
                      </div>
                      {canManage && (
                        <button
                          type="button"
                          className="btn btn-sm btn-success flex-shrink-0"
                          onClick={handleClearSchedulingBlock}
                          disabled={schedulingClearLoading || loading}
                        >
                          {schedulingClearLoading ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-1"
                                role="status"
                                aria-hidden="true"
                              />
                              Updating…
                            </>
                          ) : (
                            'Mark available for scheduling'
                          )}
                        </button>
                      )}
                    </div>
                  )}
                  <form onSubmit={handleUpdateSubmit}>
                    <div className="row">
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Name</label>
                        <input
                          type="text"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Model</label>
                        <input
                          type="text"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="model"
                          value={formData.model}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Serial Number</label>
                        <input
                          type="text"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="serialNumber"
                          value={formData.serialNumber}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Manufacturer</label>
                        <input
                          type="text"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="manufacturer"
                          value={formData.manufacturer}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Purchase Date</label>
                        <input
                          type="date"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="purchaseDate"
                          value={formData.purchaseDate}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Status</label>
                        <select
                          className="form-select"
                          style={sharedStyles.formSelect}
                          name="status"
                          value={formData.status}
                          onChange={handleInputChange}
                        >
                          <option value="Active">Active</option>
                          <option value="Inactive">Inactive</option>
                          <option value="Maintenance">Maintenance</option>
                          <option value="Repair">Repair</option>
                        </select>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Last Maintenance Date</label>
                        <input
                          type="date"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="lastMaintenanceDate"
                          value={formData.lastMaintenanceDate}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Next Maintenance Date</label>
                        <input
                          type="date"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="nextMaintenanceDate"
                          value={formData.nextMaintenanceDate}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Location</label>
                        <input
                          type="text"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="location"
                          value={formData.location}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Day Rate</label>
                        <input
                          type="number"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="dayRate"
                          value={formData.dayRate}
                          onChange={handleInputChange}
                          min="0"
                          step="0.01"
                          placeholder="Enter day rate"
                        />
                      </div>
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Hourly Rate</label>
                        <input
                          type="number"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="hourlyRate"
                          value={formData.hourlyRate}
                          onChange={handleInputChange}
                          min="0"
                          step="0.01"
                          placeholder="Enter hourly rate"
                        />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Rate per Load</label>
                        <input
                          type="number"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="loadRate"
                          value={formData.loadRate}
                          onChange={handleInputChange}
                          min="0"
                          step="0.01"
                          placeholder="Enter rate per load"
                        />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-12 mb-3">
                        <label style={sharedStyles.formLabel}>Notes</label>
                        <textarea
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="notes"
                          value={formData.notes}
                          onChange={handleInputChange}
                          rows="3"
                        />
                      </div>
                    </div>

                    <div className="modal-footer border-0 pt-0">
                      <button 
                        type="button" 
                        className="btn me-2"
                        style={sharedStyles.button.secondary}
                        onClick={handleCloseEditModal}
                        disabled={loading}
                      >
                        Cancel
                      </button>
                      <button 
                        type="submit" 
                        className="btn"
                        style={sharedStyles.button.primary}
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Updating...
                          </>
                        ) : (
                          'Update Machine'
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Machines; 